import {createElement, ReactNode} from 'react'
import {StyledMarkdown} from './style'

/**
 * Small markdown renderer tailored to Linear issue descriptions produced by the
 * bug reporter: headings, horizontal rules, GFM pipe tables, ordered/unordered
 * lists, paragraphs, and inline emphasis/code/links/images. It is intentionally
 * minimal — it is not a general-purpose markdown engine.
 */

/**
 * A link destination may be wrapped in angle brackets — `[text](<url>)` — and Linear's
 * serializer emits exactly that for URLs it auto-links, so a bare `url` cell in the
 * Environment table comes back as `[url](<url>)`. The brackets delimit the destination
 * and are not part of it: capture the two forms separately and read them via `destination`.
 */
const RE_IMAGE = /!\[([^\]]*)\]\(\s*(?:<([^>]*)>|([^)\s]+))\s*\)/y
const RE_LINK = /\[([^\]]*)\]\(\s*(?:<([^>]*)>|([^)\s]+))\s*\)/y
const RE_AUTOLINK = /<([a-zA-Z][a-zA-Z0-9+.-]*:[^<>\s]+)>/y
const RE_CODE = /`([^`]+)`/y
const RE_BOLD_ITALIC = /\*\*\*([^*]+)\*\*\*/y
const RE_BOLD = /\*\*([^*]+)\*\*|__([^_]+)__/y
const RE_STRIKE = /~~([^~]+)~~/y
const RE_ITALIC = /\*([^*]+)\*|_([^_]+)_/y

const matchAt = (re: RegExp, text: string, index: number): RegExpExecArray | null => {
    re.lastIndex = index
    return re.exec(text)
}

/** Angle-bracketed form first, then the bare one — only one of the two groups ever matches. */
const destination = (m: RegExpExecArray): string => m[2] ?? m[3]

/**
 * These descriptions round-trip through Linear from text the reporter typed, so a
 * destination is only trusted as an href when it carries a scheme that can't execute.
 * Anything else renders as plain text rather than a clickable `javascript:` payload.
 */
const SAFE_DESTINATION_RE = /^(?:https?:|mailto:)/i
const safeHref = (dest: string): string | undefined => (SAFE_DESTINATION_RE.test(dest) ? dest : undefined)

const parseInline = (text: string, keyPrefix: string): ReactNode[] => {
    const nodes: ReactNode[] = []
    let buffer = ''
    let i = 0
    let k = 0

    const flush = () => {
        if (buffer) {
            nodes.push(buffer)
            buffer = ''
        }
    }

    while (i < text.length) {
        const key = `${keyPrefix}-${k}`
        let m: RegExpExecArray | null

        if ((m = matchAt(RE_IMAGE, text, i))) {
            flush()
            nodes.push(<img key={key} src={safeHref(destination(m))} alt={m[1]} />)
        } else if ((m = matchAt(RE_LINK, text, i))) {
            flush()
            nodes.push(
                <a key={key} href={safeHref(destination(m))} target="_blank" rel="noreferrer noopener">
                    {parseInline(m[1], key)}
                </a>
            )
        } else if ((m = matchAt(RE_AUTOLINK, text, i))) {
            flush()
            nodes.push(
                <a key={key} href={safeHref(m[1])} target="_blank" rel="noreferrer noopener">
                    {m[1].replace(/^mailto:/i, '')}
                </a>
            )
        } else if ((m = matchAt(RE_CODE, text, i))) {
            flush()
            nodes.push(<code key={key}>{m[1]}</code>)
        } else if ((m = matchAt(RE_BOLD_ITALIC, text, i))) {
            flush()
            nodes.push(
                <strong key={key}>
                    <em>{parseInline(m[1], key)}</em>
                </strong>
            )
        } else if ((m = matchAt(RE_BOLD, text, i))) {
            flush()
            nodes.push(<strong key={key}>{parseInline(m[1] ?? m[2], key)}</strong>)
        } else if ((m = matchAt(RE_STRIKE, text, i))) {
            flush()
            nodes.push(<del key={key}>{parseInline(m[1], key)}</del>)
        } else if ((m = matchAt(RE_ITALIC, text, i))) {
            flush()
            nodes.push(<em key={key}>{parseInline(m[1] ?? m[2], key)}</em>)
        } else {
            buffer += text[i]
            i++
            continue
        }

        i += m[0].length
        k++
    }

    flush()
    return nodes
}

const HR_RE = /^(-{3,}|\*{3,}|_{3,})$/
const HEADING_RE = /^(#{1,6})\s+(.*)$/
const UL_RE = /^\s*[-*]\s+/
const OL_RE = /^\s*\d+\.\s+/
const TABLE_SEPARATOR_RE = /^\s*\|?[\s:|-]+\|?\s*$/

const isTableHeader = (line: string, next: string | undefined): boolean =>
    line.trim().startsWith('|') && !!next && next.includes('-') && TABLE_SEPARATOR_RE.test(next)

const isBlockStart = (line: string, next: string | undefined): boolean =>
    !line.trim() ||
    HEADING_RE.test(line) ||
    HR_RE.test(line.trim()) ||
    UL_RE.test(line) ||
    OL_RE.test(line) ||
    isTableHeader(line, next)

const splitRow = (line: string): string[] =>
    line
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map(cell => cell.replace(/\\\|/g, '|').trim())

const renderParagraph = (lines: string[], key: number): ReactNode => (
    <p key={key}>
        {lines.flatMap((line, li) => {
            const content = parseInline(line, `p${key}-${li}`)
            return li === 0 ? content : [<br key={`p${key}-br-${li}`} />, ...content]
        })}
    </p>
)

export const renderMarkdown = (md: string): ReactNode[] => {
    const lines = md.replace(/\r\n/g, '\n').split('\n')
    const blocks: ReactNode[] = []
    let i = 0
    let key = 0

    while (i < lines.length) {
        const line = lines[i]

        if (!line.trim()) {
            i++
            continue
        }

        if (HR_RE.test(line.trim())) {
            blocks.push(<hr key={key++} />)
            i++
            continue
        }

        const heading = HEADING_RE.exec(line)
        if (heading) {
            const level = Math.min(heading[1].length, 6)
            blocks.push(createElement(`h${level}`, {key: key++}, parseInline(heading[2], `h${key}`)))
            i++
            continue
        }

        if (isTableHeader(line, lines[i + 1])) {
            const header = splitRow(line)
            const headerKey = key++
            i += 2 // skip the header row and the separator row
            const rows: string[][] = []
            while (i < lines.length && lines[i].trim().startsWith('|')) {
                rows.push(splitRow(lines[i]))
                i++
            }
            blocks.push(
                <table key={headerKey}>
                    <thead>
                        <tr>
                            {header.map((cell, ci) => (
                                <th key={ci}>{parseInline(cell, `th${headerKey}-${ci}`)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, ri) => (
                            <tr key={ri}>
                                {row.map((cell, ci) => (
                                    <td key={ci}>{parseInline(cell, `td${headerKey}-${ri}-${ci}`)}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )
            continue
        }

        if (UL_RE.test(line)) {
            const listKey = key++
            const items: string[] = []
            while (i < lines.length && UL_RE.test(lines[i])) {
                items.push(lines[i].replace(UL_RE, ''))
                i++
            }
            blocks.push(
                <ul key={listKey}>
                    {items.map((item, ii) => (
                        <li key={ii}>{parseInline(item, `ul${listKey}-${ii}`)}</li>
                    ))}
                </ul>
            )
            continue
        }

        if (OL_RE.test(line)) {
            const listKey = key++
            const items: string[] = []
            while (i < lines.length && OL_RE.test(lines[i])) {
                items.push(lines[i].replace(OL_RE, ''))
                i++
            }
            blocks.push(
                <ol key={listKey}>
                    {items.map((item, ii) => (
                        <li key={ii}>{parseInline(item, `ol${listKey}-${ii}`)}</li>
                    ))}
                </ol>
            )
            continue
        }

        const paragraph: string[] = []
        while (i < lines.length && !isBlockStart(lines[i], lines[i + 1])) {
            paragraph.push(lines[i])
            i++
        }
        blocks.push(renderParagraph(paragraph, key++))
    }

    return blocks
}

export const Markdown = ({content}: {content: string}) => <StyledMarkdown>{renderMarkdown(content)}</StyledMarkdown>
